import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { debounceTime, distinctUntilChanged } from 'rxjs'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatButton } from '@angular/material/button'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatOption } from '@angular/material/core'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { PurchaseServiceOrderService } from '../../../core/services/purchase-reservation/purchase-service-order/purchase-service-order.service'
import { SupplierService } from '../../../core/services/person/supplier/supplier.service'
import { AuthService } from '../../../core/services/auth/auth.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { CurrencyInputDirective } from '../../../shared/directives/currencyInput'
import { convertCurrency } from '../../../shared/utils/utils'
import { ErrorRequestModel } from '../../../core/models/response.model'
import { EditPurchaseServiceOrderBatchModel } from '../../../core/models/purchase-reservation.model'
import { MULTIPLICADOR_VENDA_PADRAO, PERFIL } from '../../../core/constants/perfil.constants'

@Component({
  selector: 'purchase-reservation-edit-batch-modal',
  imports: [
    ReactiveFormsModule,
    MatButton,
    MatProgressSpinner,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOption,
    MatCheckboxModule,
    CurrencyInputDirective,
  ],
  templateUrl: './purchase-reservation-edit-batch-modal.component.html',
  styleUrl: './purchase-reservation-edit-batch-modal.component.scss',
  standalone: true,
})
export class PurchaseReservationEditBatchModalComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private comprasOrdemServicoService: PurchaseServiceOrderService,
    private supplierService: SupplierService,
    private authService: AuthService,
    private snackbar: SnackbarCustomService,
    private dialogRef: MatDialogRef<PurchaseReservationEditBatchModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { comprasOsIds: string[]; itens?: any[] },
  ) {
    this.comprasOsIds = data.comprasOsIds
    this.itens = data.itens ?? []
  }

  @Output() response = new EventEmitter()

  comprasOsIds: string[] = []
  itens: any[] = []
  saving = false
  suppliers: any[] = []
  form: FormGroup = new FormGroup({})
  supplierFilterForm: FormGroup = new FormGroup({})

  get isGestor(): boolean {
    return (
      this.authService.getInfosObject()?.idPerfil ==
      PERFIL.GESTOR_RESERVA_COMPRAS
    )
  }

  // Multiplicador é único para o lote inteiro — só libera edição se TODA OS selecionada
  // tiver divisor < 0,55; senão trava em 0,55 (mesma regra do modal individual, aplicada
  // ao pior caso do grupo, pra não deixar o usuário digitar um valor que o backend vai
  // ignorar silenciosamente em parte das OSs).
  get multiplicadorTravado(): boolean {
    return this.itens.some((item: any) => item.divisor == null || item.divisor >= MULTIPLICADOR_VENDA_PADRAO)
  }

  get multiplicadorEditavel(): boolean {
    return this.isGestor && !this.multiplicadorTravado
  }

  get osTravadasCount(): number {
    return this.itens.filter((item: any) => item.divisor == null || item.divisor >= MULTIPLICADOR_VENDA_PADRAO).length
  }

  // Quando editável, o padrão é o menor divisor do lote — o valor mais conservador
  // entre as OSs selecionadas; o Gestor ainda pode ajustar antes de salvar.
  private get multiplicadorPadraoDoLote(): number {
    if (this.multiplicadorTravado) return MULTIPLICADOR_VENDA_PADRAO
    const divisores = this.itens.map((item: any) => item.divisor).filter((d: any) => d != null)
    return divisores.length > 0 ? Math.min(...divisores) : MULTIPLICADOR_VENDA_PADRAO
  }

  get totalVendaSemTaxa(): number | null {
    const valores = this.itens
      .map((item: any) => item.valorVendaSemTaxa)
      .filter((valor: any) => valor != null)
    if (valores.length === 0) {
      return null
    }
    return valores.reduce((total: number, valor: number) => total + valor, 0)
  }

  get osSemVenda(): number {
    return this.itens.filter((item: any) => item.valorVendaSemTaxa == null)
      .length
  }

  get totalVendaSemTaxaDisplay(): string {
    return this.totalVendaSemTaxa != null
      ? `${convertCurrency(this.totalVendaSemTaxa)}`
      : '-'
  }

  get targetTotalDisplay(): string {
    const venda = this.totalVendaSemTaxa
    const multiplicador = this.form.get('multiplicadorVenda')?.value
    if (venda == null || multiplicador == null || multiplicador === '') {
      return '-'
    }
    return `${convertCurrency(venda * multiplicador)}`
  }

  ngOnInit(): void {
    this.createForm()
    this.createSupplierFilterForm()
    this.listSuppliers()
  }

  createSupplierFilterForm(): void {
    this.supplierFilterForm = this.formBuilder.group({
      nome: [''],
      cnpj: [''],
      cidade: [''],
    })

    this.supplierFilterForm.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
      .subscribe(() => this.listSuppliers())
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      fornecedorId: ['', Validators.required],
      valorCompra: ['', Validators.required],
      freelancer: [false],
      valorMotorista: [{ value: '', disabled: true }],
      multiplicadorVenda: [{ value: this.multiplicadorPadraoDoLote, disabled: !this.multiplicadorEditavel }],
      observacaoOs: [''],
      obsFornecedorOperador: [''],
    })

    this.form.get('freelancer')?.valueChanges.subscribe((freelancer: boolean) => {
      const control = this.form.get('valorMotorista')
      if (freelancer) {
        control?.enable()
      } else {
        control?.setValue('')
        control?.disable()
      }
    })
  }

  listSuppliers() {
    const { nome, cnpj, cidade } = this.supplierFilterForm.value
    this.supplierService.listSupplier('S', nome, cnpj, cidade).subscribe({
      next: (response: any) => {
        this.suppliers = response.data ?? []
      },
      error: () => {
        this.suppliers = []
      },
    })
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      return
    }
    this.saving = true
    const freelancer = this.form.get('freelancer')!.value
    const model: EditPurchaseServiceOrderBatchModel = {
      comprasOsIds: this.comprasOsIds,
      fornecedorId: this.form.get('fornecedorId')!.value,
      valorCompra: convertCurrency(
        this.form.get('valorCompra')!.value,
      ) as number,
      freelancer,
      valorMotorista: freelancer
        ? (convertCurrency(this.form.get('valorMotorista')!.value) as number)
        : 0,
      enviarEmail: false,
      observacaoOs: this.form.get('observacaoOs')!.value || null,
      obsFornecedorOperador:
        this.form.get('obsFornecedorOperador')!.value || null,
    }
    if (this.multiplicadorEditavel) {
      model.multiplicadorVenda = this.form.get('multiplicadorVenda')!.value
    }

    this.comprasOrdemServicoService.editComprasOsLote(model).subscribe({
      next: (response: any) => {
        this.saving = false
        const sucesso = response.data?.sucesso ?? []
        const erros = response.data?.erros ?? []
        if (erros.length === 0) {
          this.snackbar.success(
            `${sucesso.length} OS atualizada(s) com sucesso.`,
          )
        } else {
          this.snackbar.warning(
            `${sucesso.length} OS atualizada(s), ${erros.length} falharam: ${erros
              .map((e: any) => e.mensagem)
              .join('; ')}`,
          )
        }
        this.response.emit(true)
        this.close()
      },
      error: (err: ErrorRequestModel) => {
        this.saving = false
        this.snackbar.errorRequest(err)
      },
    })
  }

  close() {
    this.dialogRef.close()
  }
}
